import { useQuery } from '@tanstack/react-query';
import { queryKeys, type SearchSort } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { Button, Input, Select } from '@/components/ui';

/**
 * PRD §15's filter sidebar: category, district, date range, author and sort.
 *
 * Every control writes to the URL rather than to local state (see `SearchPage`), so a
 * filtered result list is a link someone can send.
 *
 * The author list comes from `/public/authors`, which returns only bylines that have
 * published work — a menu of authors with nothing to show is a set of choices that all
 * lead to "nothing found".
 */
export function SearchFilters({
  filters,
  sort,
  sorts,
  onChange,
  disabled,
}: {
  filters: { category?: string; district?: string; author?: string; from?: string; to?: string };
  sort: SearchSort;
  sorts: readonly SearchSort[];
  onChange: (changes: Record<string, string | undefined>) => void;
  disabled: boolean;
}) {
  const { locale, t } = useLocale();

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories(locale),
    queryFn: () => publicApi.categories(locale),
    staleTime: STALE.taxonomy,
  });
  const { data: districts } = useQuery({
    queryKey: queryKeys.districts(locale),
    queryFn: () => publicApi.districts(locale),
    staleTime: STALE.taxonomy,
  });
  const { data: authors } = useQuery({
    queryKey: queryKeys.authors(locale),
    queryFn: () => publicApi.authors(locale),
    staleTime: STALE.taxonomy,
  });

  const hasFilters = Boolean(
    filters.category || filters.district || filters.author || filters.from || filters.to,
  );

  return (
    <div className="flex flex-col gap-4" aria-label={t('search.filters')} role="group">
      <Select
        label={t('search.sort')}
        value={sort}
        disabled={disabled}
        onChange={(event) => onChange({ sort: event.target.value })}
        options={sorts.map((value) => ({ value, label: t(`search.sort.${value}`) }))}
      />

      <Select
        label={t('search.category')}
        value={filters.category ?? ''}
        disabled={disabled}
        onChange={(event) => onChange({ category: event.target.value || undefined })}
        options={[
          { value: '', label: t('filter.all') },
          ...(categories ?? []).map((category) => ({
            value: category.slug,
            label: category.name,
          })),
        ]}
      />

      <Select
        label={t('search.district')}
        value={filters.district ?? ''}
        disabled={disabled}
        onChange={(event) => onChange({ district: event.target.value || undefined })}
        options={[
          { value: '', label: t('filter.all') },
          ...(districts ?? []).map((district) => ({
            value: district.slug,
            label: district.name,
          })),
        ]}
      />

      <Select
        label={t('search.author')}
        value={filters.author ?? ''}
        disabled={disabled}
        onChange={(event) => onChange({ author: event.target.value || undefined })}
        options={[
          { value: '', label: t('filter.all') },
          ...(authors ?? []).map((author) => ({ value: author.slug, label: author.name })),
        ]}
      />

      {/*
        Two dates rather than a pair of presets: "everything since the flood" and "that
        week in August" are both ordinary newsroom questions, and either end may be the
        only one a reader knows. `max` on each keeps the range from crossing itself.
      */}
      <fieldset className="flex flex-col gap-3 border-0 p-0">
        <legend className="mb-1 text-xs font-bold uppercase tracking-wide text-ink">
          {t('search.dateRange')}
        </legend>
        <Input
          label={t('search.dateFrom')}
          type="date"
          value={filters.from ?? ''}
          max={filters.to ?? undefined}
          disabled={disabled}
          onChange={(event) => onChange({ from: event.target.value || undefined })}
        />
        <Input
          label={t('search.dateTo')}
          type="date"
          value={filters.to ?? ''}
          min={filters.from ?? undefined}
          disabled={disabled}
          onChange={(event) => onChange({ to: event.target.value || undefined })}
        />
      </fieldset>

      {hasFilters && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange({
              category: undefined,
              district: undefined,
              author: undefined,
              from: undefined,
              to: undefined,
            })
          }
        >
          {t('search.clearFilters')}
        </Button>
      )}
    </div>
  );
}
